// utils/dataHelpers.js - Утилиты для работы с данными пользователя и договора

import { API_CONSTANTS } from '../types/api';

// =============================================================================
// УТИЛИТЫ ДЛЯ РАБОТЫ С ДАННЫМИ ДОГОВОРА
// =============================================================================

/**
 * Создает пустую структуру данных договора
 */
export const createEmptyContractData = () => {
  return {
    // Ответственность сторон
    responsibility: {
      // Основной тип ответственности
      responsibilityType: 'general', // 'none', 'general', 'detailed'

      // Property seizure subsection (Изъятие имущества третьими лицами)
      propertySeizureEnabled: true,
      seizureCompensationAmount: null,      // Сумма возмещения
      seizurePenaltyAmount: 50000,          // Размер штрафа

      // Payment delay penalty subsection (Штраф за просрочку платежа)
      paymentDelayPenaltyEnabled: false,
      gracePeriodDays: 5,                   // Льготный период в днях
      penaltyType: 'percent',               // 'percent' или 'fixed'
      penaltyAmount: 1,                     // Размер штрафа (в % или рублях)
      maxPenaltyAmount: null                // Максимальный размер штрафа
    },
  };
};

/**
 * Объединяет данные договора с значениями по умолчанию
 */
export const mergeContractData = (existingData, defaultData = null) => {
  const defaults = defaultData || createEmptyContractData();
  
  if (!existingData || typeof existingData !== 'object') {
    return defaults;
  }
  
  const mergeSection = (existing, defaultSection) => {
    if (!existing || typeof existing !== 'object') {
      return defaultSection;
    }
    
    const merged = { ...defaultSection };
    Object.keys(existing).forEach(key => {
      if (existing[key] !== null && existing[key] !== undefined) {
        if (typeof existing[key] === 'object' && !Array.isArray(existing[key])) {
          merged[key] = mergeSection(existing[key], defaultSection[key] || {});
        } else {
          merged[key] = existing[key];
        }
      }
    });
    
    return merged;
  };
  
  const merged = {};
  Object.keys(defaults).forEach(section => {
    merged[section] = mergeSection(existingData[section], defaults[section]);
  });
  
  return merged;
};

/**
 * Валидирует данные договора
 */
export const validateContractData = (contractData) => {
  const errors = {};
  const warnings = {};
  
  // Валидация общих сведений
  if (!contractData.general?.signPlace?.trim()) {
    errors.general = errors.general || [];
    errors.general.push('Место подписания обязательно для заполнения');
  }
  
  if (!contractData.general?.signDate) {
    errors.general = errors.general || [];
    errors.general.push('Дата подписания обязательна для заполнения');
  }
  
  // Валидация арендодателя
  if (!contractData.landlord?.name?.trim()) {
    errors.landlord = errors.landlord || [];
    errors.landlord.push('Имя арендодателя обязательно для заполнения');
  }
  
  if (contractData.landlord?.email && !isValidEmail(contractData.landlord.email)) {
    warnings.landlord = warnings.landlord || [];
    warnings.landlord.push('Некорректный формат email арендодателя');
  }
  
  // Валидация арендатора
  if (!contractData.tenant?.name?.trim()) {
    errors.tenant = errors.tenant || [];
    errors.tenant.push('Имя арендатора обязательно для заполнения');
  }
  
  if (contractData.tenant?.email && !isValidEmail(contractData.tenant.email)) {
    warnings.tenant = warnings.tenant || [];
    warnings.tenant.push('Некорректный формат email арендатора');
  }
  
  // Валидация недвижимости
  if (!contractData.property?.address?.trim()) {
    errors.property = errors.property || [];
    errors.property.push('Адрес квартиры обязателен для заполнения');
  }
  
  if (!contractData.property?.area?.trim()) {
    errors.property = errors.property || [];
    errors.property.push('Площадь квартиры обязательна для заполнения');
  }
  
  // Валидация финансовых условий
  if (!contractData.financial?.monthlyRent?.trim()) {
    errors.financial = errors.financial || [];
    errors.financial.push('Размер арендной платы обязателен для заполнения');
  }
  
  if (contractData.financial?.monthlyRent && !isValidNumber(contractData.financial.monthlyRent)) {
    errors.financial = errors.financial || [];
    errors.financial.push('Размер арендной платы должен быть числом');
  }
  
  if (!contractData.financial?.paymentDate?.trim()) {
    errors.financial = errors.financial || [];
    errors.financial.push('Дата оплаты обязательна для заполнения');
  }
  
  // Валидация сроков
  if (!contractData.terms?.startDate) {
    errors.terms = errors.terms || [];
    errors.terms.push('Дата начала аренды обязательна для заполнения');
  }
  
  if (!contractData.terms?.endDate) {
    errors.terms = errors.terms || [];
    errors.terms.push('Дата окончания аренды обязательна для заполнения');
  }
  
  if (contractData.terms?.startDate && contractData.terms?.endDate) {
    const startDate = new Date(contractData.terms.startDate);
    const endDate = new Date(contractData.terms.endDate);
    
    if (startDate >= endDate) {
      errors.terms = errors.terms || [];
      errors.terms.push('Дата окончания должна быть позже даты начала');
    }
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors,
    warnings,
    hasWarnings: Object.keys(warnings).length > 0
  };
};

// =============================================================================
// УТИЛИТЫ ДЛЯ РАБОТЫ С ПОЛЬЗОВАТЕЛЯМИ
// =============================================================================

/**
 * Создает объект пользователя по умолчанию
 */
export const createDefaultUser = () => {
  return {
    id: '',
    email: '',
    name: '',
    createdAt: '',
    hasContract: false
  };
};

/**
 * Валидирует данные пользователя
 */
export const validateUserData = (userData) => {
  const errors = {};
  
  if (!userData.email?.trim()) {
    errors.email = 'Email обязателен для заполнения';
  } else if (!isValidEmail(userData.email)) {
    errors.email = 'Некорректный формат email';
  }
  
  if (!userData.name?.trim()) {
    errors.name = 'Имя обязательно для заполнения';
  } else if (userData.name.trim().length < 2) {
    errors.name = 'Имя должно содержать минимум 2 символа';
  }
  
  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};

// =============================================================================
// УТИЛИТЫ ДЛЯ АВТОСОХРАНЕНИЯ
// =============================================================================

/**
 * Создает debounced функцию для автосохранения
 */
export const createAutosaveDebounce = (saveFunction, delay = API_CONSTANTS.AUTOSAVE_DELAY) => {
  let timeoutId = null;
  
  return (data) => {
    if (timeoutId) {
      clearTimeout(timeoutId);
    }
    
    timeoutId = setTimeout(() => {
      saveFunction(data);
    }, delay);
  };
};

/**
 * Сравнивает два объекта данных договора на предмет изменений
 */
export const hasDataChanged = (oldData, newData) => {
  if (!oldData && !newData) return false;
  if (!oldData || !newData) return true;
  
  return JSON.stringify(oldData) !== JSON.stringify(newData);
};

/**
 * Создает копию данных договора для безопасного изменения
 */
export const cloneContractData = (data) => {
  if (!data) return createEmptyContractData();
  
  try {
    return JSON.parse(JSON.stringify(data));
  } catch (error) {
    console.error('Ошибка клонирования данных договора:', error);
    return createEmptyContractData();
  }
};

// =============================================================================
// ВСПОМОГАТЕЛЬНЫЕ ФУНКЦИИ
// =============================================================================

/**
 * Проверяет валидность email
 */
const isValidEmail = (email) => {
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  return emailRegex.test(email);
};

/**
 * Проверяет, является ли строка валидным числом
 */
const isValidNumber = (value) => {
  const num = parseFloat(value);
  return !isNaN(num) && isFinite(num) && num > 0;
};

/**
 * Получает вложенное значение по пути
 */
const getNestedValue = (obj, path) => {
  return path.split('.').reduce((current, key) => {
    return current && current[key] !== undefined ? current[key] : null;
  }, obj);
};

/**
 * Устанавливает вложенное значение по пути
 */
export const setNestedValue = (obj, path, value) => {
  const keys = path.split('.');
  const lastKey = keys.pop();
  
  const target = keys.reduce((current, key) => {
    if (!current[key] || typeof current[key] !== 'object') {
      current[key] = {};
    }
    return current[key];
  }, obj);
  
  target[lastKey] = value;
  return obj;
};

/**
 * Получает текущую дату в формате дд.мм.гггг (локальное время)
 * Используется для избежания проблем с часовыми поясами
 */
export const getCurrentDateLocal = () => {
  const now = new Date();
  const day = String(now.getDate()).padStart(2, '0');
  const month = String(now.getMonth() + 1).padStart(2, '0');
  const year = now.getFullYear();
  return `${day}.${month}.${year}`;
};

/**
 * Форматирует дату для отображения
 */
export const formatDate = (dateString) => {
  if (!dateString) return '';
  
  try {
    const date = new Date(dateString);
    return date.toLocaleDateString('ru-RU');
  } catch (error) {
    return dateString;
  }
};

/**
 * Форматирует сумму для отображения
 */
export const formatCurrency = (amount) => {
  if (!amount) return '';
  
  const num = parseFloat(amount);
  if (isNaN(num)) return amount;
  
  return new Intl.NumberFormat('ru-RU', {
    style: 'currency',
    currency: 'RUB',
    minimumFractionDigits: 0
  }).format(num);
};

/**
 * Генерирует уникальный ID
 */
export const generateId = (prefix = '') => {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 9);
  return `${prefix}${timestamp}_${random}`;
};

// =============================================================================
// ЭКСПОРТ
// =============================================================================

export default {
  // Работа с договорами
  createEmptyContractData,
  mergeContractData,
  validateContractData,
  cloneContractData,
  
  // Работа с пользователями
  createDefaultUser,
  validateUserData,
  
  // Автосохранение
  createAutosaveDebounce,
  hasDataChanged,
  
  // Утилиты
  setNestedValue,
  formatDate,
  formatCurrency,
  generateId
};